import React, {
  useState, useCallback, useEffect, useMemo,
} from 'react';
import { FtpMigrationForm } from './components/FtpMigrationForm';
import { ChooseMigrationOption } from './components/ChooseMigrationOption';
import { STEPS, initialStep } from './WordpressMigration.enum';
import useLocale from '@/hooks/useLocale';
import { commonActions, onboardingActions, sitesActions } from '@/redux/modules';
import { useDispatch, useSelector } from 'react-redux';
import { IconArrow, IconClock } from 'gatorcomponents';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { ATIVACAO_WORDPRESS_MIGRATION_FILES, ATIVACAO_WORDPRESS_MIGRATION_FTP } from '@/config/GrowthBook/constants';
import { useHistory } from 'react-router';
import WordpressMigrationBR from '@/media/onboarding/wordpressMigration/wordpress-migration-br.png';
import WordpressMigrationES from '@/media/onboarding/wordpressMigration/wordpress-migration-es.png';
import { isBRServer } from '@/utils/Validators/validation';
import { GenericSuccess } from '@/components/FullWidthTemplate/GenericSuccess';
import FilesMigration from './components/FilesMigration/FilesMigration';

const WordpressMigration = ({
  hostingId, domain, origin, exitMigrationFlow, cleanUp,
}) => {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [migrationType, setMigrationType] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const sitesReducer = useSelector(state => state.sites);
  const dispatch = useDispatch();

  const isFtpMigrationActive = useFeatureIsOn(ATIVACAO_WORDPRESS_MIGRATION_FTP);
  const isFilesMigrationActive = useFeatureIsOn(ATIVACAO_WORDPRESS_MIGRATION_FILES);

  const history = useHistory();

  const {
    routes: routesLocale,
    onboarding: onboardingLocale,
  } = useLocale();

  const showToast = useCallback(({ title, info, type }) => {
    const toast = {
      title,
      info,
      showIcon: true,
      context: type,
    };

    dispatch(commonActions.notifications.set({ toast }));
    dispatch(sitesActions.wordpressFtpMigration.reset());
  }, [dispatch]);

  const stepParam = useMemo(() => ({
    [STEPS.CHOOSE_MIGRATION_OPTION]: 'choose-option',
    [STEPS.FTP_MIGRATION_FORM]: 'ftp',
    [STEPS.FILES_MIGRATION]: 'files',
    [STEPS.SUCCESS]: 'success',
  }), []);

  const updateMigrationStatus = useCallback(() => {
    const isFinished = currentStep === STEPS.SUCCESS;

    if (isFinished && origin === 'onboarding') {
      sessionStorage.setItem('onboardingFinished', true);
    }

    dispatch(onboardingActions[origin].update.request({
      hostingId,
      domain,
      origin,
      builder: 'wordpress',
      step: `migration-${stepParam[currentStep]}`,
      finished: isFinished,
    }));
  }, [hostingId, currentStep, stepParam, dispatch, origin, domain]);

  const finishOnboarding = useCallback(() => {
    cleanUp();
    exitMigrationFlow();
    setCurrentStep(initialStep);
    history.push(routesLocale.sitesPage);
    dispatch(sitesActions.wordpressFtpMigration.reset());
  }, [cleanUp, exitMigrationFlow, history, routesLocale.sitesPage, dispatch]);

  const getChooseMigrationProps = useCallback(() => {
    const migrationFlows = {
      ftp: STEPS.FTP_MIGRATION_FORM,
      files: STEPS.FILES_MIGRATION,
    };
    const buttonsProps = {
      leftButton: {
        label: onboardingLocale.goBackButtonLabel,
        variant: 'secondary',
        onClick: exitMigrationFlow,
      },
      rightButton: {
        iconRight: <IconArrow direction="right" />,
        label: onboardingLocale.continueButtonLabel,
        variant: 'primary',
        onClick: () => setCurrentStep(migrationFlows[migrationType]),
        disabled: !migrationType,
        testId: 'choose-migration-option-continue',
      },
    };

    const locales = onboardingLocale.chooseMigrationOption;
    const options = [
      {
        id: locales.filesMigration.id,
        title: locales.filesMigration.title,
        description: locales.filesMigration.description,
        tag: locales.filesMigration.tag,
        show: isFilesMigrationActive,
      },
      {
        id: locales.ftpMigration.id,
        title: locales.ftpMigration.title,
        description: locales.ftpMigration.description,
        show: isFtpMigrationActive,
      },
    ];

    return {
      locales,
      options,
      buttonsProps,
      setMigrationType,
    };
  }, [
    onboardingLocale.chooseMigrationOption,
    onboardingLocale.continueButtonLabel,
    onboardingLocale.goBackButtonLabel,
    setMigrationType,
    migrationType,
    isFtpMigrationActive,
    isFilesMigrationActive,
    exitMigrationFlow,
  ]);

  const getFtpMigrationFormProps = useCallback(() => {
    const { loading, status } = sitesReducer.wordPressFtpMigration;
    const onSubmit = ({ formData }) => {
      dispatch(sitesActions.wordpressFtpMigration.request({
        formData,
        hostingId,
        domain,
      }));
    };

    const goBack = () => {
      setMigrationType(null);
      setCurrentStep(STEPS.CHOOSE_MIGRATION_OPTION);
    };

    const buttonsProps = {
      leftButton: {
        label: onboardingLocale.goBackButtonLabel,
        variant: 'secondary',
        onClick: goBack,
      },
      rightButton: {
        iconRight: <IconArrow direction="right" />,
        label: onboardingLocale.continueButtonLabel,
        variant: 'primary',
      },
    };
    return {
      locales: onboardingLocale.ftpMigrationForm,
      onSubmit,
      buttonsProps,
      loading,
      status,
      showToast,
    };
  }, [
    onboardingLocale.ftpMigrationForm,
    onboardingLocale.continueButtonLabel,
    onboardingLocale.goBackButtonLabel,
    dispatch,
    hostingId,
    domain,
    sitesReducer.wordPressFtpMigration,
    showToast,
  ]);

  const getMigrationSuccess = useCallback(() => {
    const imageSrc = isBRServer ? WordpressMigrationBR : WordpressMigrationES;

    const buttonsProps = {
      rightButton: {
        iconRight: <IconArrow direction="right" />,
        label: onboardingLocale.migrationSuccess.continueButtonLabel,
        variant: 'primary',
        onClick: finishOnboarding,
      },
    };
    return {
      locales: onboardingLocale.migrationSuccess,
      buttonsProps,
      imageSrc,
      cleanUp: () => setMigrationType(null),
      icon: <IconClock color="attentionDark" size="lg" />,
    };
  }, [onboardingLocale.migrationSuccess, finishOnboarding]);

  const getFilesMigrationProps = useCallback(() => {
    const onFinishMigrationRequest = () => {
      setCurrentStep(STEPS.SUCCESS);
    };

    const requestHostingData = ({ files }) => {
      dispatch(sitesActions.wordpressFileMigration.requestData({
        hostingId, files, domain, onFinish: onFinishMigrationRequest,
      }));
    };

    const goBack = () => {
      setMigrationType(null);
      setCurrentStep(STEPS.CHOOSE_MIGRATION_OPTION);
    };

    const buttonsProps = {
      leftButton: {
        label: onboardingLocale.goBackButtonLabel,
        variant: 'secondary',
        onClick: goBack,
        disabled: sitesReducer.wordPressFilesMigration.loading,
      },
      rightButton: {
        iconRight: <IconArrow direction="right" />,
        label: onboardingLocale.sendFileButtonLabel,
        variant: 'primary',
        onClick: requestHostingData,
        disabled: sitesReducer.wordPressFilesMigration.loading || selectedFiles.length < 2,
        loading: sitesReducer.wordPressFilesMigration.loading,
      },
    };

    return ({
      locales: onboardingLocale.filesMigration,
      requestHostingData,
      buttonsProps,
      loading: sitesReducer.wordPressFilesMigration.loading,
      setSelectedFiles,
    });
  }, [
    onboardingLocale,
    dispatch,
    hostingId,
    domain,
    sitesReducer.wordPressFilesMigration.loading,
    selectedFiles,
    setSelectedFiles,
  ]);

  const handleRedirectToSucces = useCallback(() => {
    if (
      sitesReducer.wordPressFtpMigration.status === 'success'
    || sitesReducer.wordPressFilesMigration.finished === true
    ) {
      setCurrentStep(STEPS.SUCCESS);
    }
  }, [
    sitesReducer.wordPressFtpMigration.status,
    sitesReducer.wordPressFilesMigration.finished,
    setCurrentStep,
  ]);

  useEffect(() => {
    handleRedirectToSucces();
  }, [handleRedirectToSucces]);


  const handleUnmount = useCallback(() => {
    dispatch(sitesActions.wordpressFileMigration.finishedRequestedInstallation(false));

    if (currentStep === STEPS.CHOOSE_MIGRATION_OPTION) {
      dispatch(sitesActions.wordpressFtpMigration.reset());
    }
  }, [dispatch, currentStep]);

  useEffect(() => () => {
    handleUnmount();
  }, [handleUnmount]);

  useEffect(() => {
    updateMigrationStatus();
  }, [updateMigrationStatus]);

  const renderStep = {
    [STEPS.CHOOSE_MIGRATION_OPTION]: <ChooseMigrationOption {...getChooseMigrationProps()} />,
    [STEPS.FTP_MIGRATION_FORM]: <FtpMigrationForm {...getFtpMigrationFormProps()} />,
    [STEPS.FILES_MIGRATION]: <FilesMigration {...getFilesMigrationProps()} />,
    [STEPS.SUCCESS]: <GenericSuccess {...getMigrationSuccess()} />,
  };

  return renderStep[currentStep];
};

export default WordpressMigration;
